/** One-click Zillow filter purge (Tom 2026-08-12: "make the button"). Requests
 * go to the backend, which only writes a control row; the scraper cron runs the
 * actual purge with the crawler's own filter code and writes the report back,
 * so the button and the crawler can never disagree. The UI requests, then polls
 * /status until the cron reports done. */
import { backendFetch } from "../../lib/backend";

export interface PurgeReason { rule: string; count: number; samples: string[]; }

export interface PurgeReport {
  applied?: boolean;
  checked?: number;
  keptMatch?: number;
  keptNoDescription?: number;
  keptTouched?: number;
  removed?: number;             // removed if applied, else "would remove"
  reasons?: PurgeReason[];
  error?: string;
}

export interface PurgeStatus {
  status: "idle" | "pending" | "running" | "done" | "error";
  mode?: "preview" | "apply";
  report?: PurgeReport;
  error?: string;
  requestedAt?: string;
  doneAt?: string;
}

export async function requestZillowPurge(token: string | null, mode: "preview" | "apply"): Promise<{ status: string }> {
  return backendFetch<{ ok: boolean; status: string }>(token, "/api/scrapers/zillow-purge/request", {
    method: "POST",
    body: { mode },
    timeoutMs: 20000,
  });
}

export async function getZillowPurgeStatus(token: string | null): Promise<PurgeStatus> {
  return backendFetch<PurgeStatus>(token, "/api/scrapers/zillow-purge/status", { timeoutMs: 20000 });
}

/** Human label for a rejection rule shown in the report. */
export function purgeReasonLabel(rule: string): string {
  const m: Record<string, string> = {
    keywords_any: "No keyword match", keywords_all: "Missing a required keyword",
    price_below_min: "Price below minimum", price_above_max: "Price above maximum",
    beds_below_min: "Too few beds", baths_below_min: "Too few baths",
    sqft_below_min: "Sqft below minimum", sqft_above_max: "Sqft above maximum",
    year_built_below_min: "Built before minimum year", year_built_above_max: "Built after maximum year",
    county: "County not in your list", county_from_zip: "County (by ZIP) not in your list",
    excluded_zip: "Excluded ZIP", zip_not_whitelisted: "ZIP not in your list",
    excluded_broker: "Excluded broker", excluded_broker_detail: "Excluded broker",
    dom_below_min: "Days-on-market below window", dom_above_max: "Days-on-market above window",
    renovated: "Fully renovated", no_match: "Matched no job",
  };
  return m[rule] ?? rule;
}
